/**
 * DeckFormV2 — V2 port of the Card Deck edit form (DeckForm.js).
 * Same shape: caller passes `deck` + `setDeck`; this component
 * renders all the fields and lets the parent own save/cancel.
 *
 * Sections:
 *   01 Basic info       — name (req), parent, primary color,
 *                         Premium + Active toggles, free view limit
 *   02 Deck descriptions — module / choose-card / see-all descriptions
 *   03 Sharing          — share image + universal link
 *   04 Module / Header / Wrapper / Search images (4 UploadTiles)
 *   05 Purchase store   — URL + text
 *
 * Media uploads route through the v1 `uploadImage(file, target)`
 * pattern: action stores `image` + `target` in Redux media slice;
 * the form's useEffect reads back, calls handleChange with the
 * remote URL, then dispatches clearMedia(). Byte-identical to v1.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadImage, clearMedia } from '../../../../actions/mediaActions';

import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  ToggleSwitch,
  LinkField,
  UploadTile,
  ParentSelectButton,
} from '../../../redesign/edit';

export default function DeckFormV2({ deck, setDeck }) {
  const dispatch = useDispatch();
  const mediaStore = useSelector((s) => s.media);

  /* Media settle — pulls the uploaded image URL back into the
     matching `deck[target]` field once Redux media populates.
     Uses functional setDeck so we don't need to close over the
     latest deck reference (and don't need to list it as a dep). */
  useEffect(() => {
    if (mediaStore?.image && mediaStore?.target) {
      const targetField = mediaStore.target;
      const url = mediaStore.image;
      setDeck((prev) => ({ ...prev, [targetField]: url }));
      dispatch(clearMedia());
    }
  }, [mediaStore, dispatch, setDeck]);

  const handleChange = (e) => {
    setDeck({ ...deck, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (name) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(uploadImage(file, name));
  };

  const removeImage = (name) => () => {
    handleChange({ target: { name, value: '' } });
  };

  const handleToggle = (name) => (e) => {
    handleChange({ target: { name, value: !!e.target.checked } });
  };

  /* Parent picker uses v1's `${id}TYPE` value contract — convert
     between deck.category_id / deck.subcategory_id and the picker
     state. */
  const handleParentChange = (value) => {
    if (value === '0') {
      setDeck({ ...deck, category_id: null, subcategory_id: null });
      return;
    }
    const m = String(value).match(/^(\d+)(CAT|SUB|AUD)$/);
    if (!m) return;
    const [, id, type] = m;
    if (type === 'CAT') {
      setDeck({ ...deck, category_id: id, subcategory_id: null });
    } else {
      setDeck({ ...deck, category_id: null, subcategory_id: id });
    }
  };
  const parentValue = (() => {
    if (deck.category_id) return String(deck.category_id) + 'CAT';
    if (deck.subcategory_id) return String(deck.subcategory_id) + 'SUB';
    return '0';
  })();

  return (
    <>
      {/* 01 Basic info */}
      <FormSection title="Basic info" num="01">
        <FieldBlock label="Deck name" required htmlFor="f-deck-name">
          <TextInput
            id="f-deck-name"
            name="name"
            value={deck.name ?? ''}
            onChange={handleChange}
            required
          />
        </FieldBlock>
        <FieldRow cols={2} minColWidth={240}>
          <FieldBlock label="Parent" hint="Category / Subcategory / Audiobook">
            <ParentSelectButton
              value={parentValue}
              onChange={handleParentChange}
              allowedTypes={['category', 'subcategory', 'audiobook']}
              showQuickTaps
            />
          </FieldBlock>
          <FieldBlock label="Primary color" hint="Used as a tint accent in the app">
            <input
              type="color"
              name="primary_color_hex"
              value={deck.primary_color_hex || '#FFFFFF'}
              onChange={handleChange}
              style={{
                width: '100%',
                height: 36,
                border: '1px solid #E8E0CF',
                borderRadius: 6,
                background: '#FAF4EA',
                padding: 4,
                cursor: 'pointer',
              }}
            />
          </FieldBlock>
        </FieldRow>
        <FieldRow cols={3} minColWidth={180}>
          <FieldBlock label="Free view limit" hint="Cards viewable before paywall">
            <TextInput
              type="number"
              name="free_view_limit"
              value={deck.free_view_limit ?? 1}
              onChange={handleChange}
              min={0}
            />
          </FieldBlock>
          <FieldBlock label="Status" noMargin>
            <div className="flex flex-wrap" style={{ gap: '10px 18px', paddingTop: 6 }}>
              <ToggleSwitch
                name="is_premium"
                checked={!!deck.is_premium}
                onChange={handleToggle('is_premium')}
                label="Premium"
              />
              <ToggleSwitch
                name="is_active"
                checked={!!deck.is_active}
                onChange={handleToggle('is_active')}
                label="Active"
              />
            </div>
          </FieldBlock>
        </FieldRow>
      </FormSection>

      {/* 02 Deck descriptions */}
      <FormSection title="Descriptions" num="02">
        <FieldBlock label="Module description" htmlFor="f-deck-mod-desc">
          <Textarea
            id="f-deck-mod-desc"
            name="module_description"
            value={deck.module_description ?? ''}
            onChange={handleChange}
            rows={3}
            placeholder="Shown on the deck's module on the home screen."
          />
        </FieldBlock>
        <FieldBlock label="Choose-card screen description" htmlFor="f-deck-desc">
          <Textarea
            id="f-deck-desc"
            name="description"
            value={deck.description ?? ''}
            onChange={handleChange}
            rows={3}
            placeholder="Body copy on the card-picker screen."
          />
        </FieldBlock>
        <FieldBlock label="See all — description" htmlFor="f-deck-seeall" noMargin>
          <Textarea
            id="f-deck-seeall"
            name="see_all_description"
            value={deck.see_all_description ?? ''}
            onChange={handleChange}
            rows={3}
            placeholder="Shown on the 'See all cards' screen."
          />
        </FieldBlock>
      </FormSection>

      {/* 03 Sharing */}
      <FormSection title="Sharing" num="03">
        <FieldRow cols={2} minColWidth={240}>
          <UploadTile
            label="Share image"
            value={deck.share_image_url}
            accept="image/*"
            onChange={handleImageUpload('share_image_url')}
            onRemove={removeImage('share_image_url')}
          />
          <FieldBlock label="Universal Link" hint="Generated automatically · share & deep links">
            <LinkField value={deck.universal_link || ''} />
          </FieldBlock>
        </FieldRow>
      </FormSection>

      {/* 04 In-app images */}
      <FormSection title="In-app images" num="04">
        <FieldRow cols={4} minColWidth={170}>
          <UploadTile
            label="Module image"
            value={deck.module_image_url}
            accept="image/*"
            onChange={handleImageUpload('module_image_url')}
            onRemove={removeImage('module_image_url')}
          />
          <UploadTile
            label="Header image"
            value={deck.header_image_url}
            accept="image/*"
            onChange={handleImageUpload('header_image_url')}
            onRemove={removeImage('header_image_url')}
          />
          <UploadTile
            label="Wrapper image"
            value={deck.wrapper_image_url}
            accept="image/*"
            onChange={handleImageUpload('wrapper_image_url')}
            onRemove={removeImage('wrapper_image_url')}
          />
          <UploadTile
            label="Search result image"
            value={deck.search_result_image_url}
            accept="image/*"
            onChange={handleImageUpload('search_result_image_url')}
            onRemove={removeImage('search_result_image_url')}
          />
        </FieldRow>
      </FormSection>

      {/* 05 Purchase store */}
      <FormSection title="Purchase store" num="05">
        <FieldBlock label="Purchase store URL" htmlFor="f-deck-store-url">
          <TextInput
            id="f-deck-store-url"
            name="purchase_store_url"
            value={deck.purchase_store_url ?? ''}
            onChange={handleChange}
            placeholder="https://…"
          />
        </FieldBlock>
        <FieldBlock label="Purchase store text" htmlFor="f-deck-store-text" noMargin>
          <Textarea
            id="f-deck-store-text"
            name="purchase_store_text"
            value={deck.purchase_store_text ?? ''}
            onChange={handleChange}
            rows={3}
            placeholder="Copy shown on the purchase prompt."
          />
        </FieldBlock>
      </FormSection>
    </>
  );
}
